import { useEffect, useState } from 'react'
import { FileText, FolderOpen, FolderTree, Layers, Paperclip } from 'lucide-react'
import { getStewardNote, type StewardNote, type StewardJobFile } from '@/lib/api'
import { Markdown } from './Markdown'

interface Props {
  noteKind: string
  /** Folder path (job) or JD code (structure). */
  id: string
  /** The reference's label, shown while the note loads. */
  title?: string
  onOpenFile: (file: { path: string; name: string; ext: string }) => void
}

const KIND_ICON: Record<string, typeof FolderOpen> = {
  job: FolderOpen,
  project: FolderTree,
  category: Layers,
  area: Layers,
}

/**
 * A clicked `steward://` reference, opened as a card in the chat — a Job's brief (job.md)
 * or a structure level's governing note (area/category/project.md). Mirrors JobCard's look;
 * loads live from /steward/note (only the reference is held in the store — never content).
 * For a job it also shows the working links (files); clicking one opens it as its own card.
 */
export function NoteCard({ noteKind, id, title, onOpenFile }: Props) {
  const [note, setNote] = useState<StewardNote | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    getStewardNote(noteKind, id)
      .then((n) => alive && setNote(n))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Failed to load'))
    return () => {
      alive = false
    }
  }, [noteKind, id])

  const Icon = KIND_ICON[note?.kind || noteKind] || FolderOpen

  return (
    <div className="max-w-[85%] rounded-2xl border border-border bg-muted/20 p-4 text-[15px] leading-relaxed text-foreground">
      <div className="mb-2 flex items-center gap-2 text-[12px] font-light text-muted-foreground">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{note?.title || title || (error ? 'Reference' : 'Loading…')}</span>
      </div>

      {error ? (
        <div className="text-[13px] text-red-500/90">Couldn't open this: {error}</div>
      ) : !note ? (
        <div className="text-[13px] font-light text-muted-foreground">Loading…</div>
      ) : (
        <>
          {note.body ? (
            <Markdown source={note.body} className="steward-prose" />
          ) : (
            <div className="text-[13px] font-light text-muted-foreground">(no notes yet)</div>
          )}

          {note.files.length > 0 && (
            <div className="mt-3 border-t border-border pt-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-light text-muted-foreground">
                <Paperclip className="h-3 w-3" /> Files
              </div>
              <div className="flex flex-wrap gap-1.5">
                {note.files.map((f: StewardJobFile) => (
                  <button
                    key={f.rel}
                    type="button"
                    onClick={() => onOpenFile({ path: f.rel, name: f.name, ext: f.ext })}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-[12px] font-light hover:bg-muted/50"
                  >
                    <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="max-w-[220px] truncate">{f.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
